import { frontendURL } from '../../../../helper/URLHelper';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import ReportsWrapper from './components/ReportsWrapper.vue';

const Index = () => import('./Index.vue');
const AgentReportsIndex = () => import('./AgentReportsIndex.vue');
const InboxReportsIndex = () => import('./InboxReportsIndex.vue');
const TeamReportsIndex = () => import('./TeamReportsIndex.vue');
const LabelReportsIndex = () => import('./LabelReportsIndex.vue');
const AgentReportsShow = () => import('./AgentReportsShow.vue');
const InboxReportsShow = () => import('./InboxReportsShow.vue');
const TeamReportsShow = () => import('./TeamReportsShow.vue');
const LabelReportsShow = () => import('./LabelReportsShow.vue');
// Legacy report components removed — old routes redirect to revised versions
const CsatResponses = () => import('./CsatResponses.vue');
const BotReports = () => import('./BotReports.vue');
const LiveReports = () => import('./LiveReports.vue');
const SLAReports = () => import('./SLAReports.vue');
const CrmReportsDashboard = () => import('./CrmReportsDashboard.vue');
const MarketingReports = () => import('./MarketingReports.vue');
const AgentAvailabilityReports = () => import('./AgentAvailabilityReports.vue');
const AgentAvailabilityShow = () => import('./AgentAvailabilityShow.vue');

// Meta granular por rota: cada relatorio checa a chave certa em vez
// de um `report_manage` unico. `conversationReportMeta` para conversa,
// `agentReportMeta` para tempo/desempenho de agente, `crmReportMeta`
// para deals/tickets/tasks/companies.
const conversationReportMeta = {
  featureFlag: FEATURE_FLAGS.REPORTS,
  permissions: ['administrator', 'report_manage', 'report_conversation_view'],
};

const agentReportMeta = {
  featureFlag: FEATURE_FLAGS.REPORTS,
  permissions: ['administrator', 'report_manage', 'report_agent_view'],
};

const crmReportMeta = {
  featureFlag: FEATURE_FLAGS.REPORTS,
  permissions: ['administrator', 'report_manage', 'report_crm_view'],
};

// Old routes redirect to revised versions
const oldReportRoutes = [
  {
    path: 'agent',
    redirect: to => ({ name: 'agent_reports_index', params: to.params }),
  },
  {
    path: 'inboxes',
    redirect: to => ({ name: 'inbox_reports_index', params: to.params }),
  },
  {
    path: 'label',
    redirect: to => ({ name: 'label_reports_index', params: to.params }),
  },
  {
    path: 'teams',
    redirect: to => ({ name: 'team_reports_index', params: to.params }),
  },
];

const revisedReportRoutes = [
  {
    path: 'agents_overview',
    name: 'agent_reports_index',
    meta: agentReportMeta,
    component: AgentReportsIndex,
  },
  {
    path: 'agents/:id',
    name: 'agent_reports_show',
    meta: agentReportMeta,
    component: AgentReportsShow,
  },

  {
    path: 'inboxes_overview',
    name: 'inbox_reports_index',
    meta: conversationReportMeta,
    component: InboxReportsIndex,
  },
  {
    path: 'inboxes/:id',
    name: 'inbox_reports_show',
    meta: conversationReportMeta,
    component: InboxReportsShow,
  },
  {
    path: 'teams_overview',
    name: 'team_reports_index',
    meta: agentReportMeta,
    component: TeamReportsIndex,
  },
  {
    path: 'teams/:id',
    name: 'team_reports_show',
    meta: agentReportMeta,
    component: TeamReportsShow,
  },
  {
    path: 'labels_overview',
    name: 'label_reports_index',
    meta: conversationReportMeta,
    component: LabelReportsIndex,
  },
  {
    path: 'labels/:id',
    name: 'label_reports_show',
    meta: conversationReportMeta,
    component: LabelReportsShow,
  },
];

export default {
  routes: [
    {
      path: frontendURL('accounts/:accountId/reports'),
      component: ReportsWrapper,
      children: [
        {
          path: '',
          redirect: to => {
            return { name: 'account_overview_reports', params: to.params };
          },
        },
        {
          path: 'overview',
          name: 'account_overview_reports',
          meta: conversationReportMeta,
          component: LiveReports,
        },
        {
          path: 'conversation',
          name: 'conversation_reports',
          meta: conversationReportMeta,
          component: Index,
        },
        ...oldReportRoutes,
        ...revisedReportRoutes,
        {
          path: 'availability',
          name: 'agent_availability_reports',
          meta: agentReportMeta,
          component: AgentAvailabilityReports,
        },
        {
          path: 'availability/:id',
          name: 'agent_availability_show',
          meta: agentReportMeta,
          component: AgentAvailabilityShow,
        },
        {
          path: 'sla',
          name: 'sla_reports',
          meta: conversationReportMeta,
          component: SLAReports,
        },
        {
          path: 'csat',
          name: 'csat_reports',
          meta: conversationReportMeta,
          component: CsatResponses,
        },
        {
          path: 'brain',
          name: 'bot_reports',
          meta: agentReportMeta,
          component: BotReports,
        },
        {
          path: 'brain/:id',
          name: 'bot_reports_show',
          meta: agentReportMeta,
          component: BotReports,
        },
        // Rota antiga: /reports/bot redireciona pra /reports/brain
        {
          path: 'bot',
          redirect: to => ({ name: 'bot_reports', params: to.params }),
        },
        {
          path: 'bot/:id',
          redirect: to => ({ name: 'bot_reports_show', params: to.params }),
        },
        {
          path: 'crm',
          name: 'crm_reports',
          meta: crmReportMeta,
          component: CrmReportsDashboard,
        },
        {
          path: 'crm/agents/:agentId',
          name: 'crm_reports_agent',
          meta: crmReportMeta,
          component: () => import('./AgentCrmDetail.vue'),
        },
        {
          path: 'campaigns',
          name: 'campaigns_reports',
          meta: crmReportMeta,
          component: () => import('./CampaignsReports.vue'),
        },
        {
          path: 'landing-pages',
          name: 'landing_pages_reports',
          meta: crmReportMeta,
          component: () => import('./LandingPagesReports.vue'),
        },
        {
          path: 'marketing',
          name: 'marketing_reports',
          meta: crmReportMeta,
          component: MarketingReports,
        },
      ],
    },
  ],
};
